import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { MODES } from '@lumapps/customizations';
import { ExternalLinkResource, Resource } from '@lumapps/journeys/types';
import { AuthenticatedIframe } from '@lumapps/lumx-layouts/components/AuthenticatedIframe';
import { Dialog, Size } from '@lumapps/lumx/react';
import { addQueryParamsToUrl, createUrl } from '@lumapps/router/utils';

import { broadcastDistributeCreate } from '../../routes';

import './index.scss';

export interface DistributeResourceDialogProps {
    /** Whether the dialog is visible or not (state) */
    isOpen: boolean;
    /** Wrapper function on dialog close */
    onClose: () => void;
    /** Wrapper function on dialog visibility change */
    onVisibilityChange?: (isVisible: boolean) => void;
    /** The resource to distribute */
    resource: Exclude<Resource, ExternalLinkResource>;
}

const CLASSNAME = 'distribute-resource-dialog';

/**
 * Displays a dialog to create a broadcast from a resource
 *
 * @param DistributeResourceDialogProps
 * @returns DistributeResourceDialog
 */
export const DistributeResourceDialog: React.FC<DistributeResourceDialogProps> = ({
    isOpen,
    onClose,
    onVisibilityChange,
    resource,
}) => {
    const { block, element } = useClassnames(CLASSNAME);
    const parentElement = React.useRef(null);

    const baseRoute = broadcastDistributeCreate({ resourceId: resource.id, resourceType: resource.type });
    const url = createUrl(baseRoute);
    const src = addQueryParamsToUrl(url, {
        modes: MODES.NO_SIDE_NAV_BO,
    });

    return (
        <Dialog
            className={block()}
            isOpen={isOpen}
            parentElement={parentElement}
            size={Size.huge}
            onClose={onClose}
            onVisibilityChange={onVisibilityChange}
        >
            <AuthenticatedIframe
                title="distribute-resource"
                loadingType="src"
                src={src}
                iframeProps={{ frameBorder: '0', className: element('iframe') }}
            />
        </Dialog>
    );
};

export default DistributeResourceDialog;
